import { useNavigate, useParams, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Store, Globe, ExternalLink } from "lucide-react";
import { StoreForm } from "@/components/stores/store-form";
import { WebhookSyncSection } from "@/components/stores/webhook-sync-section";
import { useStore, useUpdateStore } from "@/hooks/use-stores";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function EditStorePage() {
  const { storeId } = useParams({ from: "/_app/stores/$storeId/edit" });
  const navigate = useNavigate();
  const { data: store, isLoading } = useStore(storeId);
  const updateStore = useUpdateStore();

  async function handleSubmit(values: {
    name: string;
    url: string;
    consumerKey: string;
    consumerSecret: string;
  }) {
    try {
      await updateStore.mutateAsync({ id: storeId, ...values });
      toast.success("Trgovina ažurirana");
      navigate({ to: "/settings" });
    } catch (e: any) {
      toast.error(e.message || "Greška pri ažuriranju trgovine");
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <Skeleton className="h-5 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-80 w-full" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Link to="/settings">
          <Button variant="ghost" size="icon-xs"><ArrowLeft className="h-3.5 w-3.5" /></Button>
        </Link>
        <Store className="h-4 w-4" />
        <h2 className="text-sm font-medium">{store?.name || "Uredi trgovinu"}</h2>
        {store?.returnsEnabled && (
          <Badge variant="outline" className="bg-green-500/15 text-green-400 border-green-500/20">
            Povrati aktivni
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3">
        <StoreForm
          storeId={storeId}
          initialValues={store}
          onSubmit={handleSubmit}
          loading={updateStore.isPending}
          submitLabel="Ažuriraj"
        />

        <div className="flex flex-col gap-3">
          <WebhookSyncSection storeId={storeId} />

          {store?.slug && (
            <div className="ring-1 ring-foreground/10">
              <div className="border-b bg-muted/50 px-4 py-2">
                <span className="text-xs font-medium">Pregled forme</span>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <div
                  className="rounded overflow-hidden border"
                  style={{ borderColor: (store.primaryColor || "#4F46E5") + "40" }}
                >
                  <div
                    className="px-3 py-2 flex items-center gap-2"
                    style={{ backgroundColor: (store.primaryColor || "#4F46E5") + "15" }}
                  >
                    {store.logoUrl ? (
                      <img src={store.logoUrl} alt="" className="h-5 w-5 rounded object-contain" />
                    ) : (
                      <div
                        className="h-5 w-5 rounded"
                        style={{ backgroundColor: store.primaryColor || "#4F46E5" }}
                      />
                    )}
                    <span className="text-xs font-medium truncate">{store.name}</span>
                  </div>
                  <div className="px-3 py-2 space-y-1.5">
                    <div className="h-2 w-3/4 bg-muted rounded" />
                    <div className="h-2 w-1/2 bg-muted rounded" />
                    <div className="h-5 rounded border mt-2" />
                    <div className="h-5 rounded border" />
                    <div
                      className="h-5 rounded mt-2"
                      style={{ backgroundColor: store.primaryColor || "#4F46E5" }}
                    />
                  </div>
                </div>
                <a
                  href={`${window.location.origin}/return/${store.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Globe className="h-3 w-3" />
                  <span className="truncate">/return/{store.slug}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              </div>
            </div>
          )}

          <div className="ring-1 ring-foreground/10">
            <div className="border-b bg-muted/50 px-4 py-2">
              <span className="text-xs font-medium">Info</span>
            </div>
            <div className="p-3 flex flex-col gap-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono text-[10px]">{store?.id?.slice(0, 8)}</span>
              </div>
              {store?.url && (
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">URL</span>
                  <a
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-1 truncate"
                  >
                    <span className="truncate">{store.url.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
